import { unitConstants, units } from './constants/common';

type UnitKey = keyof typeof units;
type UnitValue = (typeof units)[UnitKey];

type ConversionMap = Record<UnitValue, UnitValue>;

// @ts-expect-error - TS2741: Property 'units' is missing in type '{ L: "gal"; mL: "fl oz"; kg: "lb"; g: "oz";...
export const conversionMap: ConversionMap = {
  [units.liters]: units.gallons,
  [units.milliLiters]: units.fluidOunces,
  [units.kiloGrams]: units.pounds,
  [units.grams]: units.ounces,
  [units.centimeters]: units.inches,
  [units.gallons]: units.liters,
  [units.fluidOunces]: units.milliLiters,
  [units.pounds]: units.kiloGrams,
  [units.ounces]: units.grams,
  [units.inches]: units.centimeters,
};

interface AttributeValue {
  unit: UnitValue;
  value: number;
}

type AttributeMap = Record<string, AttributeValue>;

interface OptionsUnit {
  value: string;
  imperial: string;
  metric: string;
  imperialBaseConversion: number;
  metricBaseConversion: number;
}

interface OptionsMetadata {
  value: string;
  units: OptionsUnit[];
}

interface ConvertBaseUnitParams {
  isImperial?: boolean;
  classOfMeasurement: string;
  baseUnitOfMeasurement: string;
  currentUnitOfMeasurement: string;
  optionsComponentsMetadata: OptionsMetadata[];
  value: number;
}

interface ConvertUnitParams {
  isImperial?: boolean;
  optionsComponentsMetadata: OptionsMetadata[];
  value: number;
  unit?: string;
}

export class ConversionUtility {
  static toggleUnit = (imperial: boolean, unit: UnitValue): UnitValue => {
    let needsConversion = false;

    if (imperial) {
      if (
        ![
          units.fluidOunces,
          units.ounces,
          units.pounds,
          units.gallons,
          // @ts-expect-error - TS2345: Argument of type 'UnitValue' is not assignable to parameter of type '"gal" | "fl...
        ].includes(unit)
      ) {
        needsConversion = true;
      }
    } else if (
      ![units.milliLiters, units.grams, units.kiloGrams, units.liters].includes(
        // @ts-expect-error - TS2345: Argument of type 'UnitValue' is not assignable to parameter of type '"L" | "mL" ...
        unit,
      )
    ) {
      needsConversion = true;
    }

    return needsConversion ? conversionMap[unit] : unit;
  };

  // inch <-> centimeter
  static inchToCenti = (inch?: number): number | undefined =>
    inch ? inch * unitConstants.cmInInches : inch;

  static centiToInch = (cm?: number): number | undefined =>
    cm ? cm / unitConstants.cmInInches : cm;

  // pound <-> kilogram
  static poundToKilo = (lb?: number): number | undefined =>
    lb ? lb / unitConstants.KiloInlb : lb;

  static kiloToPound = (kg?: number): number | undefined =>
    kg ? kg * unitConstants.KiloInlb : kg;

  // gram <-> ounce
  static gramsToOunces = (grams?: number): number | undefined =>
    grams ? grams / unitConstants.OuncesInGrams : grams;

  static ouncesToGrams = (ounces?: number): number | undefined =>
    ounces ? ounces * unitConstants.OuncesInGrams : ounces;

  // gallon <-> liter
  static gallonsToLiters = (gallons?: number): number | undefined =>
    gallons ? gallons * unitConstants.GallonsInLiters : gallons;

  static litersToGallons = (liters?: number): number | undefined =>
    liters ? liters / unitConstants.GallonsInLiters : liters;

  // fluid ounce <-> milliliter
  static millitersToFluidOunces = (milliters?: number): number | undefined =>
    milliters ? milliters / unitConstants.FluidOuncesInMilliliters : milliters;

  static fluidOuncesToMilliters = (fluidOunces?: number): number | undefined =>
    fluidOunces
      ? fluidOunces * unitConstants.FluidOuncesInMilliliters
      : fluidOunces;

  // cbm <-> cbf
  static cbmTocbf = (cbm?: number): number | undefined =>
    cbm ? 35.3147 * cbm : cbm;

  static cbfTocbm = (cbf?: number): number | undefined =>
    cbf ? cbf / 35.3147 : cbf;

  static convertItemToMetric = (attribute: AttributeMap): AttributeMap => {
    const temp: AttributeMap = {};

    Object.keys(attribute || {}).forEach(key => {
      const attr = attribute[key];

      if (['inch', units.inches].includes(attr.unit)) {
        const value = ConversionUtility.inchToCenti(attr.value);
        temp[key] = { unit: units.centimeters, value: value ?? attr.value };
      } else if (attr.unit === units.pounds) {
        const value = ConversionUtility.poundToKilo(attr.value);
        temp[key] = { unit: units.kiloGrams, value: value ?? attr.value };
      } else if (attr.unit === units.fluidOunces) {
        const value = ConversionUtility.fluidOuncesToMilliters(attr.value);
        temp[key] = { unit: units.milliLiters, value: value ?? attr.value };
      } else if (attr.unit === units.gallons) {
        const value = ConversionUtility.gallonsToLiters(attr.value);
        temp[key] = { unit: units.liters, value: value ?? attr.value };
      } else {
        temp[key] = attr;
      }
    });

    return temp;
  };

  static convertItemToImperial = (attribute: AttributeMap): AttributeMap => {
    const temp: AttributeMap = {};

    Object.keys(attribute || {}).forEach(key => {
      const attr = attribute[key];

      if (attr?.unit === units.centimeters) {
        const value = ConversionUtility.centiToInch(attr.value);
        temp[key] = { unit: units.inches, value: value ?? attr.value };
      } else if (attr?.unit === units.kiloGrams) {
        const value = ConversionUtility.kiloToPound(attr.value);
        temp[key] = { unit: units.pounds, value: value ?? attr.value };
      } else if (attr?.unit === units.milliLiters) {
        const value = ConversionUtility.millitersToFluidOunces(attr.value);
        temp[key] = { unit: units.fluidOunces, value: value ?? attr.value };
      } else if (attr?.unit === units.liters) {
        const value = ConversionUtility.litersToGallons(attr.value);
        temp[key] = { unit: units.gallons, value: value ?? attr.value };
      } else {
        temp[key] = attr;
      }
    });

    return temp;
  };

  static covertToBaseUnit = ({
    isImperial = false,
    classOfMeasurement,
    baseUnitOfMeasurement,
    currentUnitOfMeasurement,
    optionsComponentsMetadata = [],
    value,
  }: ConvertBaseUnitParams): number | null => {
    const temp = optionsComponentsMetadata.find(
      x => x.value === classOfMeasurement,
    );
    if (!temp) return null;

    const tempUnit = temp.units.find(
      x => x[isImperial ? 'imperial' : 'metric'] === currentUnitOfMeasurement,
    );
    if (!tempUnit) return null;

    const covertedValue =
      value *
      tempUnit[isImperial ? 'imperialBaseConversion' : 'metricBaseConversion'];

    const baseUnit = temp.units.find(
      x => x[isImperial ? 'imperial' : 'metric'] === baseUnitOfMeasurement,
    );

    if (!baseUnit) return null;

    return (
      covertedValue /
      baseUnit[isImperial ? 'imperialBaseConversion' : 'metricBaseConversion']
    );
  };

  static convertOrderUnit = (
    isImperial: boolean,
    {
      value,
      unitOfMeasurement,
      optionsComponentsMetadata,
    }: {
      value: number;
      unitOfMeasurement: string;
      optionsComponentsMetadata: OptionsMetadata[];
    },
  ): number => {
    const found = optionsComponentsMetadata
      .flatMap(d => d.units)
      .find(u => [u.imperial, u.metric, u.value].includes(unitOfMeasurement));

    if (!found) return value;

    return isImperial
      ? value * (found.imperialBaseConversion / found.metricBaseConversion)
      : value * (found.metricBaseConversion / found.imperialBaseConversion);
  };

  static convertUnitToMetric = ({
    isImperial = false,
    optionsComponentsMetadata,
    value,
    unit,
  }: ConvertUnitParams): number => {
    if (!isImperial) return value;
    if (!unit) return value;

    const imperialUnit = optionsComponentsMetadata
      .flatMap(d => d.units)
      .find(c => [c.imperial, c.metric, c.value].includes(unit));

    if (imperialUnit) {
      return this.convertOrderUnit(false, {
        unitOfMeasurement: unit,
        optionsComponentsMetadata,
        value,
      });
    }

    return value;
  };

  static convertUnitToImperial = ({
    isImperial = false,
    optionsComponentsMetadata,
    value,
    unit,
  }: ConvertUnitParams): number => {
    if (isImperial) return value;
    if (!unit) return value;

    const imperialUnit = optionsComponentsMetadata
      .flatMap(d => d.units)
      .find(c => [c.imperial, c.metric, c.value].includes(unit));

    if (imperialUnit) {
      return this.convertOrderUnit(true, {
        unitOfMeasurement: unit,
        optionsComponentsMetadata,
        value,
      });
    }

    return value;
  };
}
